import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import {
  ArrowRightIcon,
  CloseIcon,
  PaperPlaneFilledIcon,
  PlusIcon,
  SparkleIcon,
  StopIcon,
} from '@utils/icons';
import { followUpsOf, resolveAiLink } from '@utils/aiChat';
import { AiFollowUps } from '@components/ui/AiFollowUps/AiFollowUps';
import { DashboardCard } from './_shared/DashboardCard';
import { CardMessages } from './aiAgentChat/CardMessages';
import { useAiChat } from './aiAgentChat/useAiChat';
import styles from './aiAgentCard.module.css';
import { isImeComposing } from '@utils/ime';

const storageKey = (siteId: string) => `dorca.aiAgentCard.conv.${siteId}`;

// The same four openers as the full page, by the same keys — one question per
// tool with a live data source behind it. Sharing the keys is the point: two
// lists would drift, and the failure mode of drift here is a chip that asks
// something the assistant cannot answer. `EmptyState.tsx` carries the argument
// for which four, and for why workforce and productivity are not among them.
const PROMPT_KEYS = [
  'aiAgent.suggestion.issues',
  'aiAgent.suggestion.schedule',
  'aiAgent.suggestion.activities',
  'aiAgent.suggestion.safety',
] as const;

interface IAiAgentCardProps {
  /** Reports whether the card is in its collapsed state (no conversation).
      The dashboard uses it to shrink the card's grid cell to the minimum
      height while collapsed and restore the saved height once a
      conversation expands in place. */
  onCollapsedChange?: (collapsed: boolean) => void;
}

/**
 * Dashboard AI Agent card. Collapsed it shows a live input + prompt chips;
 * the first question expands it in place into an inline conversation
 * (text + nav-links). The active conversation persists across visits via
 * sessionStorage, and "Open in full view" hands it off to the full page.
 */
export function AiAgentCard({ onCollapsedChange }: IAiAgentCardProps = {}) {
  const { t } = useTranslation();
  const { siteId = '' } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { messages, conversationId, isStreaming, status, error, send, stop, restore, reset } =
    useAiChat(siteId);
  // The receipt's time is the SITE's, like every operational time on the dashboard.
  const tz = useSelector(selectSiteTz);

  // Lazy initialiser: if sessionStorage already has a conversation for this
  // site, start expanded so the card doesn't flash collapsed then expand.
  // The actual conversation restore happens in the effect below.
  const [expanded, setExpanded] = useState(() => {
    if (!siteId) return false;
    return Boolean(sessionStorage.getItem(storageKey(siteId)));
  });
  const [value, setValue] = useState('');
  const restoredRef = useRef(false);

  useEffect(() => {
    onCollapsedChange?.(!expanded);
  }, [expanded, onCollapsedChange]);

  // The restore body is an Effect Event so the mount-once effect re-keys only
  // on `siteId`, not on `restore`'s identity (read at call time). `restoredRef`
  // keeps it mount-once; the `key={siteId}` on the card (in DashboardPage)
  // remounts it on a site switch so a new site re-restores.
  const runRestore = useEffectEvent((stored: string) => {
    const fallback = () => {
      // Conversation gone upstream (404) or a transport error: drop the stale
      // key and collapse back to the initial input + chips state.
      sessionStorage.removeItem(storageKey(siteId));
      setExpanded(false);
    };
    void restore(stored)
      .then((loaded) => {
        if (!loaded) fallback();
      })
      .catch(fallback);
  });

  // Restore a persisted conversation once on mount (persists across visits).
  useEffect(() => {
    if (restoredRef.current || !siteId) return;
    restoredRef.current = true;
    const stored = sessionStorage.getItem(storageKey(siteId));
    if (!stored) return;
    runRestore(stored);
  }, [siteId]);

  // Persist the conversation id as soon as the backend assigns one.
  useEffect(() => {
    if (siteId && conversationId) {
      sessionStorage.setItem(storageKey(siteId), conversationId);
    }
  }, [siteId, conversationId]);

  const submit = useCallback(
    (query: string) => {
      const text = query.trim();
      if (!text || isStreaming || !siteId) return;
      setExpanded(true);
      setValue('');
      void send(text);
    },
    [isStreaming, send, siteId],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !isImeComposing(e)) {
        e.preventDefault();
        submit(value);
      }
    },
    [submit, value],
  );

  const handleNewConversation = useCallback(() => {
    // Drop the current thread + its persisted id and return to the fresh
    // collapsed state (input + prompt chips), ready for a new conversation.
    reset();
    if (siteId) sessionStorage.removeItem(storageKey(siteId));
    setValue('');
    setExpanded(false);
  }, [reset, siteId]);

  const openFullView = useCallback(() => {
    const queryString = conversationId
      ? `?conversation=${encodeURIComponent(conversationId)}`
      : '';
    navigate(`/ai-agent/${encodeURIComponent(siteId)}${queryString}`);
  }, [conversationId, navigate, siteId]);

  const onLinkClick = useCallback(
    (url: string) => {
      // The transcript's "view in full view" nudge, which builds its own in-app
      // path. Still through the guard, which admits in-app paths only: the
      // answer's own links are anchors built from the navigation catalog, so
      // nothing here has a reason to leave the app.
      const path = resolveAiLink(url);
      if (path !== null) navigate(path);
    },
    [navigate],
  );

  const disabled = !siteId;
  // The latest answer's next questions, docked above the input in an open card
  // — held back while a turn runs, and while the reader types, so a chip can
  // never race their own question. Sent as if typed (`AiFollowUps` says why).
  const lastMessage = messages[messages.length - 1];
  const followUps =
    expanded && !isStreaming && value.trim() === '' && lastMessage?.role === 'assistant'
      ? followUpsOf(lastMessage)
      : [];
  // Not while streaming either — but then the button is the stop button.
  const canSend = value.trim().length > 0 && !disabled;

  const headerRight = expanded ? (
    <div className={styles.headerActions}>
      <button
        type="button"
        className={styles.newChatBtn}
        onClick={handleNewConversation}
      >
        <PlusIcon size={13} />
        {t('dashboard.aiAgent.newConversation')}
      </button>
      <button type="button" className={styles.fullViewBtn} onClick={openFullView}>
        {t('dashboard.aiAgent.openFullView')}
        <ArrowRightIcon size={13} />
      </button>
      <button
        type="button"
        className={styles.collapseBtn}
        onClick={() => setExpanded(false)}
        aria-label={t('dashboard.aiAgent.collapse')}
      >
        <CloseIcon size={15} />
      </button>
    </div>
  ) : undefined;

  return (
    <DashboardCard
      title={t('dashboard.aiAgent.title')}
      titleAppearance="prose"
      titleIcon={<span className={styles.titleIconInfo}><SparkleIcon /></span>}
      subtitle={expanded ? undefined : t('dashboard.aiAgent.subtitle')}
      subtitleInline
      headerRight={headerRight}
      bodyClassName={`${styles.body} ${expanded ? styles.bodyExpanded : styles.bodyCollapsed}`}
    >
      {expanded && (
        <CardMessages
          messages={messages}
          isStreaming={isStreaming}
          error={error}
          siteId={siteId}
          conversationId={conversationId}
          onLinkClick={onLinkClick}
          status={status}
          tz={tz}
        />
      )}
      <AiFollowUps questions={followUps} onPick={submit} layout="row" />
      <div className={styles.inputWrap}>
        <input
          className={styles.input}
          type="text"
          placeholder={
            messages.length > 0
              ? t('dashboard.aiAgent.followUpPlaceholder')
              : t('dashboard.aiAgent.placeholder')
          }
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        {/* While a turn runs, send becomes stop — the full page's swap, in the
            same place, so the hand that sent the question can take it back. */}
        {isStreaming ? (
          <button
            type="button"
            className={styles.sendButton}
            onClick={stop}
            aria-label={t('aiAgent.input.stop')}
          >
            <StopIcon size={12} />
          </button>
        ) : (
          <button
            type="button"
            className={styles.sendButton}
            onClick={() => submit(value)}
            disabled={!canSend}
            aria-label={t('dashboard.aiAgent.send')}
          >
            <PaperPlaneFilledIcon />
          </button>
        )}
      </div>
      {!expanded && (
        <div className={styles.chips}>
          {PROMPT_KEYS.map((key) => {
            const label = t(key);
            return (
              <button
                key={key}
                type="button"
                className={styles.chip}
                onClick={() => submit(label)}
              >
                <ArrowRightIcon size={11} />
                {label}
              </button>
            );
          })}
        </div>
      )}
    </DashboardCard>
  );
}
